import { Download, FileImage, FileType, File } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const LOGO_VARIANTS = [
  {
    label: "Full Color",
    preview: "/brand/velocity_offshore_full_color.png",
    files: [
      { format: "SVG", path: "/brand/velocity_offshore_full_color.svg", icon: FileType, note: "Vector — best for print & business cards" },
      { format: "PNG", path: "/brand/velocity_offshore_full_color.png", icon: FileImage, note: "High-res transparent background" },
      { format: "JPG", path: "/brand/velocity_offshore_full_color.jpg", icon: FileImage, note: "Web & email use" },
      { format: "EPS", path: "/brand/velocity_offshore_full_color.eps", icon: File, note: "Adobe Illustrator source" },
      { format: "PDF", path: "/brand/velocity_offshore_full_color.pdf", icon: File, note: "Print-ready vector" },
    ],
  },
  {
    label: "Black & White",
    preview: "/brand/velocity_offshore_bw.png",
    files: [
      { format: "SVG", path: "/brand/velocity_offshore_bw.svg", icon: FileType, note: "Vector — best for print & business cards" },
      { format: "PNG", path: "/brand/velocity_offshore_bw.png", icon: FileImage, note: "High-res transparent background" },
      { format: "JPG", path: "/brand/velocity_offshore_bw.jpg", icon: FileImage, note: "Web & email use" },
      { format: "EPS", path: "/brand/velocity_offshore_bw.eps", icon: File, note: "Adobe Illustrator source" },
      { format: "PDF", path: "/brand/velocity_offshore_bw.pdf", icon: File, note: "Print-ready vector" },
    ],
  },
];

const BRAND_COLORS = [
  { name: "Navy Deep", hex: "#0F2848", hsl: "213 65% 17%" },
  { name: "Royal Blue", hex: "#2151A0", hsl: "214 66% 38%" },
  { name: "Teal", hex: "#3098D0", hsl: "201 63% 50%" },
  { name: "Lime", hex: "#8FC040", hsl: "86 51% 50%" },
];

const handleDownload = (path: string, filename: string) => {
  const a = document.createElement("a");
  a.href = path;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const BrandAssets = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-[hsl(213,65%,17%)] text-white py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <p className="text-sm uppercase tracking-[0.25em] text-[hsl(201,63%,50%)] mb-3 font-medium">
            Brand Assets
          </p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Velocity Offshore Logos
          </h1>
          <p className="text-lg text-white/70 max-w-2xl">
            Always use the latest approved logos below for business cards, email signatures, presentations, and client communications.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 space-y-16">
        {/* Logo Variants */}
        {LOGO_VARIANTS.map((variant) => (
          <section key={variant.label}>
            <h2 className="text-2xl font-semibold text-foreground mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {variant.label}
            </h2>
            <Card className="overflow-hidden border border-border">
              {/* Preview */}
              <div className="bg-white p-8 md:p-12 flex items-center justify-center border-b border-border">
                <img
                  src={variant.preview}
                  alt={`Velocity Offshore logo — ${variant.label}`}
                  className="max-h-32 md:max-h-40 w-auto object-contain"
                />
              </div>
              {/* Download links */}
              <CardContent className="p-0">
                {variant.files.map((file, i) => {
                  const Icon = file.icon;
                  const filename = file.path.split("/").pop() || "";
                  return (
                    <div key={file.format}>
                      <button
                        onClick={() => handleDownload(file.path, filename)}
                        className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors text-left group"
                      >
                        <div className="flex items-center gap-4">
                          <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                          <div>
                            <span className="font-medium text-foreground">{file.format}</span>
                            <p className="text-sm text-muted-foreground">{file.note}</p>
                          </div>
                        </div>
                        <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </button>
                      {i < variant.files.length - 1 && <Separator />}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </section>
        ))}

        {/* Brand Colors Reference */}
        <section>
          <h2 className="text-2xl font-semibold text-foreground mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Brand Colors
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BRAND_COLORS.map((color) => (
              <Card key={color.name} className="overflow-hidden border border-border">
                <div className="h-20" style={{ backgroundColor: color.hex }} />
                <CardContent className="p-4">
                  <p className="font-medium text-foreground text-sm">{color.name}</p>
                  <p className="text-xs text-muted-foreground font-mono mt-1">{color.hex}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="pb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Usage Guidelines
          </h2>
          <div className="prose prose-sm text-muted-foreground max-w-none space-y-3">
            <ul className="space-y-2 list-disc pl-5">
              <li><strong className="text-foreground">Business cards & print:</strong> Use the SVG, EPS, or PDF formats for the sharpest results at any size.</li>
              <li><strong className="text-foreground">Email signatures:</strong> Use the PNG (transparent) or JPG format.</li>
              <li><strong className="text-foreground">Dark backgrounds:</strong> Use the full-color variant — the teal and lime elements provide contrast.</li>
              <li><strong className="text-foreground">Single-color printing:</strong> Use the black & white variant.</li>
              <li><strong className="text-foreground">Minimum clear space:</strong> Maintain padding equal to the height of the "V" icon mark on all sides.</li>
            </ul>
          </div>
        </section>

        <div className="text-center pb-12">
          <p className="text-xs text-muted-foreground">
            Bookmark this page — it always contains the latest approved logos.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            <a href="/" className="text-primary hover:underline">← Back to Velocity Offshore</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default BrandAssets;
